/* eslint-disable no-unused-vars */
import React, {useState, useEffect, useCallback} from 'react'
import {useNavigate, useLocation} from 'react-router-dom'
import {useIntl} from 'react-intl'
import * as Yup from 'yup'
import {useFormik} from 'formik'

import {KTCard, KTCardBody, Select, Loading, Datepicker} from '../../../components'
import {PageTitle} from '../../../core'
import {useDispatch, useSelector} from 'react-redux'
import {userTaskCreate} from '../../../redux/slices/usertask.slice'
import {allUserLookup, basicLookup} from '../../../redux/slices/lookup.slice'
import {
  useMemoizedPageUrl,
  useMemoizedValidationSchema,
  useMemoizedFormValues,
  useMemoizedBreadcrumbs,
} from '../../../utils/performanceOptimizations'

const selectLookupData = (state) => ({
  allUserLookupList: state.lookup.allUserLookupList,
  TaskPriority: state.lookup.TaskPriority,
  loading: state.lookup.loading,
})

const selectUserTaskData = (state) => ({
  isRedirect: state.userTask.isRedirect,
  loading: state.userTask.loading,
})

function Create() {
  const intl = useIntl()
  const navigate = useNavigate()
  const location = useLocation()
  const pageUrl = useMemoizedPageUrl(location)
  const {allUserLookupList, TaskPriority, loading} = useSelector(selectLookupData)
  const {isRedirect, loading: createLoading} = useSelector(selectUserTaskData)
  const dispatch = useDispatch()
  const [userIds, setUserIds] = useState([])

  const validSchema = useMemoizedValidationSchema(
    () =>
      Yup.object().shape({
        Name: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
        UserIds: Yup.array()
          .of(Yup.string())
          .required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
        TaskDate: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
        TaskPriorityId: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
      }),
    [intl]
  )

  useEffect(() => {
    dispatch(allUserLookup())
    dispatch(basicLookup('TaskPriority'))
  }, [dispatch])

  const formValues = useMemoizedFormValues({
    Name: '',
    UserIds: [],
    TaskDate: new Date(),
    TaskPriorityId: '',
  })

  const handleSubmit = useCallback(
    async (values, {setSubmitting, resetForm}) => {
      setSubmitting(true)
      dispatch(userTaskCreate(values))
      resetForm()
    },
    [dispatch]
  )

  const formik = useFormik({
    initialValues: formValues,
    validationSchema: validSchema,
    onSubmit: handleSubmit,
  })

  const breadcrumbs = useMemoizedBreadcrumbs(
    () => [
      {
        title: intl.formatMessage({id: 'BC.USERTASK'}),
        path: '/user/task',
        isSeparator: false,
        isActive: false,
      },
      {
        title: '',
        path: '',
        isSeparator: true,
        isActive: false,
      },
    ],
    [intl]
  )

  useEffect(() => {
    if (isRedirect && isRedirect.id) {
      if (userIds.length > 1) {
        navigate(`/${pageUrl[1]}/${pageUrl[2]}/`)
      } else {
        navigate(`/${pageUrl[1]}/${pageUrl[2]}/detail/${isRedirect.id}`)
      }
    }
  }, [isRedirect, userIds, navigate, pageUrl])

  return (
    <>
      <PageTitle breadcrumbs={breadcrumbs}>{intl.formatMessage({id: 'BC.USERTASK.ADD'})}</PageTitle>
      <div className='row gy-10 gx-xl-10'>
        <div className='col-xl-12'>
          <KTCard>
            <KTCardBody>
              <form className='form' onSubmit={formik.handleSubmit} noValidate>
                <div className='row mb-6'>
                  <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                    {intl.formatMessage({id: 'USERTASK.CREATE.NAME'})}
                  </label>
                  <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                    <input
                      type='text'
                      required
                      className='form-control form-control-solid mb-3 mb-lg-0'
                      autoComplete='off'
                      disabled={formik.isSubmitting}
                      {...formik.getFieldProps('Name')}
                    />
                    {formik.touched.Name && formik.errors.Name && (
                      <div className='fv-plugins-message-container'>
                        <div className='fv-help-block'>
                          <span role='alert'>{formik.errors.Name}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className='row mb-6'>
                  <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                    {intl.formatMessage({id: 'USERTASK.CREATE.USERID'})}
                  </label>
                  <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                    <Select
                      data={allUserLookupList}
                      required
                      {...formik.getFieldProps('UserIds')}
                      isMulti
                      closeMenuOnSelect={false}
                      onChange={(item) => {
                        const newData = []
                        item?.map((data) => newData.push(data.value))
                        formik.setFieldValue('UserIds', newData)
                        setUserIds(newData)
                      }}
                    />
                    {formik.touched.UserIds && formik.errors.UserIds && (
                      <div className='fv-plugins-message-container'>
                        <div className='fv-help-block'>
                          <span role='alert'>{formik.errors.UserIds}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className='row mb-6'>
                  <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                    {intl.formatMessage({id: 'USERTASK.CREATE.TASKDATE'})}
                  </label>
                  <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                    <Datepicker
                      {...formik.getFieldProps('TaskDate')}
                      onChange={([date]) => formik.setFieldValue('TaskDate', date)}
                    />
                  </div>
                </div>

                <div className='row mb-6'>
                  <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                    {intl.formatMessage({id: 'USERTASK.CREATE.TASKPRIORITYID'})}
                  </label>
                  <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                    <Select
                      data={TaskPriority}
                      {...formik.getFieldProps('TaskPriorityId')}
                      onChange={(item) => formik.setFieldValue('TaskPriorityId', item.value)}
                    />
                    {formik.touched.TaskPriorityId && formik.errors.TaskPriorityId && (
                      <div className='fv-plugins-message-container'>
                        <div className='fv-help-block'>
                          <span role='alert'>{formik.errors.TaskPriorityId}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className='row pt-15'>
                  <div className='col-lg-9 fv-row fv-plugins-icon-container d-flex gap-3 justify-content-center'>
                    <button
                      className='btn btn-light'
                      onClick={() => navigate(`/${pageUrl[1]}/${pageUrl[2]}`)}
                    >
                      {intl.formatMessage({id: 'BUTTON.CANCEL'})}
                    </button>
                    <button
                      type='submit'
                      className='btn btn-primary'
                      disabled={formik.isSubmitting || !formik.isValid || !formik.touched}
                    >
                      <span className='indicator-label'>
                        {intl.formatMessage({id: 'BUTTON.SUBMIT'})}
                      </span>
                      {formik.isSubmitting && (
                        <span className='indicator-progress'>
                          {intl.formatMessage({id: 'PLEASE'})}
                          <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                        </span>
                      )}
                    </button>
                  </div>
                </div>
              </form>
            </KTCardBody>
          </KTCard>
        </div>
      </div>

      {(loading | createLoading) > 0 && <Loading />}
    </>
  )
}
export {Create}
